import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'semantic-ui-react';
import api from '../utils/api';
import ChangeHistory, { KindToggles, isEntryVisible } from './ChangeHistory';

/**
 * Every saved change to one record (event editor History tab): when, who, what, version.
 * All kinds shown by default; toggles hide texts / replies. `version` changing (after a save) reloads it.
 */
export default function RecordHistory({ collection, id, version, only }) {
	const [kinds, setKinds] = useState(only ?? ['edit', 'notification', 'confirmation']);
	const [entries, setEntries] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const request = useRef(0);

	const load = useCallback(async () => {
		const reqId = ++request.current;
		setLoading(true);
		setError(null);
		try {
			const data = await api.getHistory(collection, id);
			if (reqId === request.current) setEntries(data.entries ?? []);
		}
		catch (ex) {
			if (reqId === request.current) setError(ex);
		}
		finally {
			if (reqId === request.current) setLoading(false);
		}
	}, [collection, id]);

	useEffect(() => { if (id) load(); }, [id, load]);

	// history is written by a trigger right after a save: give it a moment, then reload
	const firstVersion = useRef(version);
	useEffect(() => {
		if (version === firstVersion.current) return;
		const timers = [setTimeout(load, 2500), setTimeout(load, 8000)];
		return () => timers.forEach(clearTimeout);
	}, [version, load]);

	const counts = {};
	['edit', 'notification', 'confirmation'].forEach(k => { counts[k] = entries.filter(e => isEntryVisible(e, [k])).length; });

	return (
		<div>
			<div className='ch-panel-tools'>
				<KindToggles kinds={kinds} onChange={setKinds} counts={loading ? undefined : counts} only={only} />
				<Button basic compact size='small' icon='refresh' content='Refresh' onClick={load} disabled={loading} />
			</div>
			<ChangeHistory
				entries={entries}
				kinds={kinds}
				loading={loading && entries.length === 0}
				error={error}
				emptyText={kinds.length === 0
					? 'Turn on Edits, Notifications or Replies to see changes.'
					: 'No changes recorded yet. Changes are recorded from now on, each time this is saved.'}
			/>
		</div>
	);
}
